import { StatusBar, Modal, PrimaryButton, SecondaryButton, SnackbarStatus } from '@weareopenr/component-library';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import styles from './EditOptionGroupExternalSystemsModal.module.scss';
import { IOptionGroup } from '../../types/menu.types';
import { SetPublicationExternalSystems } from '../CreateOptionGroupModal/PublicationChannels/SetPublicationExternalSystems';
import { useExternalSystems } from '../../hooks/useExternalSystems';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import type { IOptionGroupValues } from '../CreateOptionGroupModal/types';
import { formatUpdateOptionGroupRequestBody, getOptionGroupDefaultValues } from '../../helpers/optionGroupHelpers';
import { ButtonContainer } from '../ButtonContainer/ButtonContainer';
import { updateOptionGroup } from '../../api/updateOptionGroup/updateOptionGroup';
import { useSelectedPriceScheme } from '../../hooks/useSelectedPriceScheme/useSelectedPriceScheme';
import { QueryKeys } from '../../hooks/QueryKeys';
import { useQueryClient } from '@tanstack/react-query';

interface IEditOptionGroupExternalSystemsModalProps {
  isOpen: boolean;
  closeModal: () => void;
  optionGroup: IOptionGroup;
  itemId?: string;
}

const Content = ({ optionGroup, closeModal, itemId }: Omit<IEditOptionGroupExternalSystemsModalProps, 'isOpen'>) => {
  const defaultValues = useMemo(() => getOptionGroupDefaultValues(optionGroup), []);
  const queryClient = useQueryClient();
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const {
    watch,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<IOptionGroupValues>({ defaultValues });
  const { data: externalSystems = [] } = useExternalSystems();
  const { selectedPriceSchemeId } = useSelectedPriceScheme();
  const selectedExternalSystems = watch('externalSystems');

  const onSubmit = async (data: IOptionGroupValues) => {
    clearError();
    try {
      const updateOptionGroupRequestBody = formatUpdateOptionGroupRequestBody(data, itemId);

      await updateOptionGroup(optionGroup.optionGroupId, updateOptionGroupRequestBody, selectedPriceSchemeId);
      await queryClient.invalidateQueries({
        queryKey: itemId ? [QueryKeys.item, itemId] : [QueryKeys.optionGroups],
      });
      closeModal();

      showNotification({ status: SnackbarStatus.SUCCESS, message: 'Your changes have been successfully saved' });
    } catch (error) {
      showNotification({
        status: SnackbarStatus.ERROR,
        message: error instanceof Error ? error.message : 'Something went wrong whilst updating the group',
      });
    }
  };
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <SetPublicationExternalSystems
        externalSystems={externalSystems}
        selectedExternalSystems={selectedExternalSystems}
        setValue={setValue}
        optionGroup={optionGroup}
      />
      <ButtonContainer
        left={
          <SecondaryButton type="button" size="full-width" buttonStyle="outline" onClick={closeModal}>
            Close
          </SecondaryButton>
        }
        right={
          <PrimaryButton size="full-width" type="submit" loading={isSubmitting} disabled={isSubmitting}>
            Save
          </PrimaryButton>
        }
      />
      <div className={styles['error-banner']}>
        {errorMessage && <StatusBar message={errorMessage} variant="error" />}
      </div>
    </form>
  );
};
export const EditOptionGroupExternalSystemsModal = ({
  isOpen,
  closeModal,
  optionGroup,
  itemId,
}: IEditOptionGroupExternalSystemsModalProps) => {
  return (
    <Modal open={isOpen} closeModal={closeModal}>
      <Content optionGroup={optionGroup} closeModal={closeModal} itemId={itemId} />
    </Modal>
  );
};
